import i18next from 'i18next';
import { ReactElement, ReactNode } from 'react';
import { Button } from 'react-bootstrap';
import { ChevronLeft, ChevronRight } from 'react-bootstrap-icons';
import { PagedQueryResult } from '../../hooks/usePagedQuery';
import { getVisiblePageNumbers } from '../../util/number-utils';

export type DataTablePaginationState = Pick<
  PagedQueryResult<unknown>,
  'page' | 'pageCount' | 'pageSize' | 'totalCount' | 'isFetching' | 'setPage'
>;

type DataTablePaginationProps = {
  pagination: DataTablePaginationState;
};

type PageButtonProps = {
  isActive?: boolean;
  isDisabled: boolean;
  ariaLabel?: string;
  onClick: () => void;
  children: ReactNode;
};

const PageButton = ({ isActive = false, isDisabled, ariaLabel, onClick, children }: PageButtonProps): ReactElement => {
  return (
    <Button
      variant="link"
      aria-label={ariaLabel}
      aria-current={isActive ? 'page' : undefined}
      disabled={isDisabled}
      onClick={onClick}
      className={`d-inline-flex align-items-center justify-content-center p-0 rounded-2 border-0 text-decoration-none small ${
        isActive ? 'fw-semibold' : 'text-muted'
      }`}
      style={{
        minWidth: 32,
        height: 32,
        background: isActive ? 'var(--bs-primary-bg-subtle)' : 'transparent',
        color: isActive ? 'var(--bs-primary)' : undefined,
      }}>
      {children}
    </Button>
  );
};

const DataTablePagination = ({ pagination }: DataTablePaginationProps): ReactElement | null => {
  const { page, pageCount, pageSize, totalCount, isFetching, setPage } = pagination;

  if (totalCount === 0) {
    return null;
  }

  const from: number = page * pageSize + 1;
  const to: number = Math.min((page + 1) * pageSize, totalCount);

  const renderControls = (): ReactElement => {
    return (
      <div className="d-flex align-items-center gap-1">
        <PageButton
          ariaLabel={i18next.t('generic.previousPage')}
          isDisabled={isFetching || page <= 0}
          onClick={() => setPage(page - 1)}>
          <ChevronLeft />
        </PageButton>

        {getVisiblePageNumbers(page, pageCount).map((pageNumber: number | null, index: number) => {
          if (pageNumber === null) {
            return (
              <span
                key={`ellipsis-${index}`}
                className="d-inline-flex align-items-center justify-content-center text-muted"
                style={{ minWidth: 32, height: 32 }}>
                ...
              </span>
            );
          }

          return (
            <PageButton
              key={pageNumber}
              isActive={pageNumber === page}
              isDisabled={isFetching && pageNumber !== page}
              onClick={() => setPage(pageNumber)}>
              {pageNumber + 1}
            </PageButton>
          );
        })}

        <PageButton
          ariaLabel={i18next.t('generic.nextPage')}
          isDisabled={isFetching || page >= pageCount - 1}
          onClick={() => setPage(page + 1)}>
          <ChevronRight />
        </PageButton>
      </div>
    );
  };

  return (
    <div className="d-flex justify-content-between align-items-center flex-wrap gap-3 px-3 py-3 border-top">
      <span className="text-muted small">
        {i18next.t('generic.rangeOf', { from, to, totalCount })}
      </span>

      {pageCount > 1 && renderControls()}
    </div>
  );
};

export default DataTablePagination;
